/**
 * Touch-target style constants — WCAG 2.5.5 conformance helpers.
 *
 * CLAUDE.md targets WCAG 2.1 AA / 44×44 px minimum touch targets across the
 * frontend. Many small icon-only ghost buttons (dismiss, prev/next, gear,
 * etc.) render at 32×32 (`h-8 w-8`) for chrome-density reasons — bumping
 * the visual to 44×44 would crowd narrow chat columns and bloat dropdown
 * triggers.
 *
 * The canonical fix per the WCAG 2.5.5 Understanding-doc is to extend the
 * *interactive target* (the bit that registers pointer input) without
 * changing the visual button: a transparent `::before` pseudo-element
 * anchored 6 px outside the button's box in all directions turns a 32×32
 * visual into a 44×44 effective hit area.
 *
 * History:
 * - #4386 / PR #4396 — first usage in `ProfessionalMatchResults` (carousel
 *   dismiss + prev/next). HIT_AREA_44_CLASS lived locally in that file.
 * - #4398 / this module — lifted here so the next contributor adding a
 *   32×32 ghost button has one place to import from.
 *
 * Usage:
 *   import { HIT_AREA_44 } from '@/lib/styles/touch-targets';
 *   <Button variant="ghost" size="sm" className={HIT_AREA_44}>…</Button>
 *
 * Adjacent-target spacing: the 6 px extension means two adjacent buttons
 * need ≥ 12 px (`gap-3`) of separation or their hit areas overlap. WCAG
 * 2.5.5 disallows overlap between distinct interactive controls.
 *
 * Anchored to Shadcn `<Button variant="ghost" size="sm">` box geometry —
 * a different variant/size will render a different box and silently break
 * the `-6px` math. The `forbidden-touch-target-shapes.test.ts` fence
 * catches drift back to bare `h-8 w-8 p-0` on a ghost button.
 */
export const HIT_AREA_44 =
  "relative h-8 w-8 p-0 before:absolute before:inset-[-6px] before:content-['']";

/**
 * Box-agnostic 44 px hit-area expansions (#5953).
 *
 * Unlike `HIT_AREA_44` (which also pins the 32×32 `h-8 w-8 p-0` visual box),
 * these helpers ONLY add the transparent `::before` pseudo-element that
 * extends the interactive target — they leave the visual box to the caller's
 * own `h-/w-` classes. Use them for dense / toolbar-aligned controls whose
 * visual footprint is load-bearing and must NOT change (a `min-h-11` floor
 * would inflate the box and misalign the layout context):
 *
 *   - `HIT_AREA_44_FROM_40` — a 40×40 box (`w-10` + `h-10`) → `-2px` each side.
 *   - `HIT_AREA_44_FROM_36` — a 36×36 box (`h-9 w-9`)       → `-4px` each side.
 *   - `HIT_AREA_44_FROM_32` — a 32×32 box (`w-8` + `h-8`)   → `-6px` each side.
 *   - `HIT_AREA_44_FROM_20` — a 20×20 box (`h-5 w-5`)       → `-12px` each side.
 *   - `HIT_AREA_44_FROM_16` — a 16×16 box (`h-4 w-4`)       → `-14px` each side.
 *
 * The inset is `(44 − box) / 2` px on every edge, so the effective pointer
 * target reaches exactly 44×44 px while the rendered box is unchanged. The
 * pseudo-element is absolutely positioned, so it never affects the parent
 * flex/grid row height — an adjacent `h-10 <Select>` / legend swatches stay
 * aligned. Each constant keeps the `HIT_AREA_44` substring so the
 * `button.test.ts` source-fence recognises it as a satisfied 44 px floor.
 *
 * Usage (compose with the control's existing box classes):
 *   className={`w-10 ${SORT_CONTROL_STYLE} ${HIT_AREA_44_FROM_40}`}
 *
 * Adjacent-target spacing (WCAG 2.5.5 — no overlap between distinct
 * controls): the expansion reaches `(44 − box) / 2` px per edge. The sort
 * toolbars separate `w-10` controls with `gap-2` (8 px); two `-2px` insets
 * add only 4 px of combined reach, under the 8 px gap → no overlap. The
 * legend's clear-X (`-12px`) is the only interactive control in its row
 * (the swatches are `aria-hidden` decorations), so its reach cannot overlap
 * a sibling target.
 */
const HIT_AREA_44_BEFORE = "relative before:absolute before:content-['']";
export const HIT_AREA_44_FROM_40 = `${HIT_AREA_44_BEFORE} before:inset-[-2px]`;
export const HIT_AREA_44_FROM_36 = `${HIT_AREA_44_BEFORE} before:inset-[-4px]`;
export const HIT_AREA_44_FROM_32 = `${HIT_AREA_44_BEFORE} before:inset-[-6px]`;
export const HIT_AREA_44_FROM_20 = `${HIT_AREA_44_BEFORE} before:inset-[-12px]`;
export const HIT_AREA_44_FROM_16 = `${HIT_AREA_44_BEFORE} before:inset-[-14px]`;

/**
 * Vertical-only 44 px hit-area expansion for a 32×32 box in a DENSE
 * HORIZONTAL row (#8730 peer-review).
 *
 * `HIT_AREA_44_FROM_32` extends symmetrically on all four edges (`-6px`
 * each side). That is correct in isolation, but breaks down for
 * side-by-side 32×32 buttons separated by only a 1px (`gap-px`) gap — e.g.
 * the copy/export/gear/edit action row in `MessageActions.tsx`. Two
 * symmetric ±6px extensions across a 1px gap overlap by 11px, and the
 * later DOM sibling wins hit-testing across the ENTIRE overlap band (no
 * z-index tiebreak), which drags the effective mis-tap boundary ~5.5px
 * before the natural midpoint between the two icons — a real, not
 * theoretical, mis-tap risk. Widening the gap to the module's own
 * documented ≥12px adjacent-spacing floor is not an option: it would
 * visibly de-densify the row (a rendering change out of scope for a
 * touch-target fix).
 *
 * `HIT_AREA_44_VERTICAL_FROM_32` resolves this by dropping the horizontal
 * extension entirely (`before:inset-x-0`) and reaching the full `-6px` only
 * on the vertical axis (`before:inset-y-[-6px]`) — a 32×44 effective hit
 * box, not 44×44. Two adjacent buttons then have ZERO horizontal overlap
 * regardless of gap size. This is still WCAG-conformant: 2.5.8 AA (Target
 * Size Minimum) sets a 24×24 px floor, and the unextended 32px box width
 * already clears it — only the vertical dimension needed help to reach the
 * AAA-aligned 44px target this module otherwise targets on both axes.
 *
 * Scope: use ONLY for controls in a tight horizontal row where the
 * adjacent-sibling overlap of the symmetric variant is the specific
 * problem being solved. For an isolated or vertically-adjacent 32×32
 * control, prefer `HIT_AREA_44_FROM_32` — the symmetric variant remains
 * correct there and gives a strictly larger hit area.
 */
export const HIT_AREA_44_VERTICAL_FROM_32 = `relative before:absolute before:content-[''] before:inset-x-0 before:inset-y-[-6px]`;

/**
 * Vertical-only 44 px hit-area expansion for a ~19 px-tall text badge
 * (`AiGeneratedBadge`'s `withDisclaimer` tooltip trigger, #8908).
 *
 * Same rationale as `HIT_AREA_44_VERTICAL_FROM_32`: the box's WIDTH already
 * clears 44 px on its own (icon + label text + padding), so a symmetric
 * `HIT_AREA_44_FROM_*` would over-extend horizontally for no reason — and
 * where the badge is a flex row's only child (e.g. `ClaimsList.tsx`), any
 * literal padding-based approach would add visible empty space instead of
 * an invisible hit area. Only the vertical axis needs the assist: 19 px
 * box + 2×13 px inset = 45 px ≥ 44.
 */
export const HIT_AREA_44_VERTICAL_FROM_19 = `relative before:absolute before:content-[''] before:inset-x-0 before:inset-y-[-13px]`;
